import {
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  OnInit,
  output,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { Router } from '@angular/router';

import { AuthService } from '../../../auth/auth.service';
import { ToastService } from '../../../core/services/toast.service';
import { ProgramService } from '../../../core/services/program.service';
import { PIInvolvementType } from '../../../shared/models/program.models';
import {
  CreateIncidentPayload,
  UpdateIncidentPayload,
  CdvPayload,
  CdvInvolvement,
  Incident,
  deriveStage,
  PartiesType,
} from '../../../shared/models/incident.models';
import { IncidentService } from '../../../core/services/incident.service';
import { SelectComponent, SelectOption } from '../../../shared/components/select/select';
import { MultiselectDropdownComponent } from '../../../shared/components/multiselect-dropdown/multiselect-dropdown';
import { DatePickerComponent } from '../../../shared/components/date-picker/date-picker';
import { TaxonomyMemberService } from '../../../core/services/taxonomy-member.service';
import { DataElementService } from '../../../core/services/data-element.service';
import { ReportRecipientService } from '../../../core/services/report-recipient.service';
import { ReportRecipient } from '../../../shared/models/report-recipient.models';
import {
  SituationTaxonomyMember,
  ResponseTaxonomyMember,
} from '../../../shared/models/taxonomy-member.models';
import { AddPartyModalComponent } from './add-party-modal/add-party-modal';
import { FileUploaderComponent } from '../../../shared/components/file-uploader/file-uploader';
import { ConfirmModalComponent } from '../../../shared/components/confirm-modal/confirm-modal';
import { Person, personTagline } from '../../../shared/models/person.models';
import { apiErrorMessage } from '../../../shared/utils/api-error';
import { PersonService } from '../../../core/services/person.service';

interface DataElementRow {
  order: number;
  configuredDataElementId: string;
  label: string;
  value: string;
  completed: boolean;
  required: boolean;
  dataType: string;
  listItems: string[];
  /** For List Item: option values are CDE list item ids. */
  options: SelectOption[];
  piInvolvementTypeId: string | null;
  /**
   * List Item value saved as text by older versions (before list items were stored by id) that
   * no longer matches any item (renamed or removed). Shown as its own option and sent back as
   * text so it isn't wiped.
   */
  legacyListValue?: string | null;
}

interface Party {
  id: string;
  personId: string;
  personTypeCode: string | null;
  piInvolvementTypeId: string;
  name: string;
  /** Admin payor (clients) or job title (staff), shown next to the name. */
  tagline: string;
  role: string;
  dataElements: DataElementRow[];
}

interface ReportRow {
  recipientId: string;
  recipient: string;
  date: string;
  reference: string;
  note: string;
}

interface DocumentRow {
  id: string;
  name: string;
  type: string;
  documentUrl: string;
}

interface AdditionalDataRow {
  element: string;
  value: string;
}

@Component({
  selector: 'app-incident-record',
  imports: [
    NgClass,
    NgTemplateOutlet,
    SelectComponent,
    MultiselectDropdownComponent,
    DatePickerComponent,
    AddPartyModalComponent,
    FileUploaderComponent,
    ConfirmModalComponent,
  ],
  templateUrl: './incident-record.html',
})
export class IncidentRecordComponent implements OnInit {
  incident = input<Incident | null>(null);
  saved = output<void>();
  saveFailed = output<void>();

  protected router = inject(Router);
  private el = inject(ElementRef);
  private auth = inject(AuthService);
  private toast = inject(ToastService);
  private programService = inject(ProgramService);
  private incidentService = inject(IncidentService);
  private dataElementService = inject(DataElementService);
  private taxonomyMemberService = inject(TaxonomyMemberService);
  private reportRecipientService = inject(ReportRecipientService);
  private personService = inject(PersonService);
  private destroyRef = inject(DestroyRef);

  // ── Stage derivation ──────────────────────────────────────────────────────

  protected currentStage = computed(() => deriveStage(this.incident()));

  // ── Stage visibility (incident state × user permissions) ─────────────────
  // Each stage is independent. effectiveAccessForStage() derives the real access
  // using the "Full covers up to stage X" rule — see AuthService for details.

  protected showMiddle = computed(
    () =>
      ['B', 'C', 'D', 'closed'].includes(this.currentStage()) &&
      this.auth.effectiveAccessForStage('Incidents', 'B') !== null,
  );

  protected showFollowUp = computed(
    () =>
      ['C', 'D', 'closed'].includes(this.currentStage()) &&
      this.auth.effectiveAccessForStage('Incidents', 'C') !== null,
  );

  protected showLower = computed(
    () =>
      ['D', 'closed'].includes(this.currentStage()) &&
      this.auth.effectiveAccessForStage('Incidents', 'D') !== null,
  );

  // ── Flag locks (flags win over permissions) ───────────────────────────────

  protected topLocked = computed(() => !!this.incident()?.stageACompletionFlag);
  protected stageCLocked = computed(() => !!this.incident()?.stageCCompletionFlag);
  protected stageBLocked = computed(() => !!this.incident()?.stageBCompletionFlag);

  protected middleLocked = computed(
    () =>
      !!this.incident()?.stageDPQCompletionFlag &&
      !!this.incident()?.stageDComplianceCompletionFlag,
  );

  protected lowerLocked = computed(
    () =>
      !!this.incident()?.stageDPQCompletionFlag &&
      !!this.incident()?.stageDPQSignoffDate &&
      !!this.incident()?.stageDComplianceCompletionFlag &&
      !!this.incident()?.stageDComplianceSignoffDate,
  );

  // ── Edit rights per stage ─────────────────────────────────────────────────
  // canInteractWithStage = Full | Edition | Creation (not just Read/Search).
  // Flags are the second gate applied in the template.

  protected canEditTop = computed(() => this.auth.canInteractWithStage('Incidents', 'A'));
  private canOverrideTopLock = computed(() => this.auth.canOverrideStageLock('Incidents', 'A'));

  // topDisabled: locked by flag unless the user has Full access on a higher stage (JCCA-72).
  // A closed (Done) incident is read-only for everyone, overrides included (JCCA-189).
  protected topDisabled = computed(
    () =>
      this.currentStage() === 'closed' ||
      (this.topLocked() && !this.canOverrideTopLock()) ||
      !this.canEditTop(),
  );

  protected canEditMiddle = computed(() => this.auth.canInteractWithStage('Incidents', 'B'));
  protected canEditFollowUp = computed(() => this.auth.canInteractWithStage('Incidents', 'C'));
  protected canEditLower = computed(() => this.auth.canInteractWithStage('Incidents', 'D'));
  protected lowerDisabled = computed(() => this.lowerLocked() || !this.canEditLower());

  // ── Workflow reversal ("Return to Previous Stage") ────────────────────────
  // Available in B, C, D and Done (not A) to users who can interact with the
  // current stage (Done counts as D, same rule as its Save). It clears the
  // completion fields of the stage it returns to.

  protected previousStage = computed<'A' | 'B' | 'C' | 'D' | null>(() => {
    const stage = this.currentStage();
    if (stage === 'B') return 'A';
    if (stage === 'C') return 'B';
    if (stage === 'D') return this.incident()?.stageCApplicable === false ? 'B' : 'C';
    if (stage === 'closed') return 'D';
    return null;
  });

  protected canReturnToPreviousStage = computed(() => {
    const stage = this.currentStage();
    return (
      this.previousStage() !== null &&
      this.auth.canInteractWithStage('Incidents', stage === 'closed' ? 'D' : stage)
    );
  });

  protected showReturnModal = signal(false);
  protected returning = signal(false);

  protected returnModalMessage = computed(() => {
    let msg =
      'This action will clear all completion fields for the current stage and move the incident back to the previous stage.';
    if (this.isDirty()) msg += ' Your unsaved changes will also be saved.';
    return msg;
  });

  private nextPartyKey = 0;

  // ── PI Involvement Types ──────────────────────────────────────────────────

  private rawInvolvementTypes = signal<PIInvolvementType[]>([]);

  private personTypeNames = signal<Map<string, string>>(new Map());

  /** Label under a person's name: the name from the API, else the person type for its code. */
  protected personTypeLabel(party: { role: string; personTypeCode: string | null }): string {
    if (party.role) return party.role;
    return party.personTypeCode ? (this.personTypeNames().get(party.personTypeCode) ?? '') : '';
  }

  protected getInvolvementTypeOptions(personTypeCode: string | null): SelectOption[] {
    return this.rawInvolvementTypes()
      .filter((t) => {
        if (personTypeCode === 'CL') return t.includeForClients === 1;
        if (personTypeCode === 'JS') return t.includeForStaff === 1;
        return t.includeForOthers === 1;
      })
      .map((t) => ({ value: t.id, label: t.name }));
  }

  // ── Cascade dropdowns ────────────────────────────────────────────────────

  protected substantiveAreaOptions = signal<SelectOption[]>([]);
  protected substantiveAreaLoading = signal(false);

  protected programOptions = signal<SelectOption[]>([]);
  protected programLoading = signal(false);

  protected locationOptions = signal<SelectOption[]>([]);
  protected locationLoading = signal(false);

  // ── TOP SECTION form state ────────────────────────────────────────────────

  protected substantiveArea = signal('');
  protected program = signal('');
  protected location = signal('');
  protected incidentDate = signal(''); // YYYY-MM-DDTHH:MM

  protected readonly partiesTypeOptions: { id: PartiesType; label: string }[] = [
    { id: 'single', label: 'Single' },
    { id: 'multiple', label: 'Multiple' },
    { id: 'na', label: 'N/A' },
  ];
  protected partiesType = signal<PartiesType>('na');
  protected expandedPartyId = signal<string | null>(null);
  protected parties = signal<Party[]>([]);
  protected showAddClientModal = signal(false);
  protected showAddOtherModal = signal(false);
  protected others = signal<Party[]>([]);
  protected expandedOtherId = signal<string | null>(null);
  private dataElementSchema = signal<DataElementRow[]>([]);
  protected situationTaxonomyMembers = signal<SituationTaxonomyMember[]>([]);
  protected responseTaxonomyMembers = signal<ResponseTaxonomyMember[]>([]);
  private situationTaxonomyId = signal<string>('');
  private reportRecipients = signal<ReportRecipient[]>([]);

  // ── Middle section ────────────────────────────────────────────────────────

  protected middleDataElements = signal<DataElementRow[]>([]);
  private incidentDataElementsSub?: Subscription;
  protected showAddReportModal = signal(false);
  protected editingReportIndex = signal<number | null>(null);
  protected selectedReportRecipient = signal('');
  protected reportDraft = signal({ recipientId: '', referenceNumber: '', date: '', note: '' });

  protected canAddParty = computed(() => {
    const type = this.partiesType();
    if (type === 'na') return false;
    if (type === 'single') return this.parties().length === 0;
    return true;
  });

  protected selectedCategoryIds = signal<string[]>([]);
  protected selectedResponseIds = signal<string[]>([]);

  protected situationCategoryOptions = computed(() =>
    this.situationTaxonomyMembers().map((m) => ({
      id: m.situationCategoryId,
      label: m.situationCategoryName,
    })),
  );
  protected responseCategoryOptions = computed(() =>
    this.responseTaxonomyMembers().map((m) => ({
      id: m.responseCategoryId,
      label: m.responseCategory.name,
    })),
  );

  protected narrative = signal('');
  protected additionalNotes = signal('');

  protected stageAFlag = signal(false); // "Submitted For Supervisory Review"

  // ── MIDDLE SECTION form state ─────────────────────────────────────────────

  protected requiredRecipients = computed(() => this.reportRecipients().map((r) => r.name));

  protected requiredRecipientOptions = computed(() => [
    { value: '', label: 'All' },
    ...this.reportRecipients().map((r) => ({ value: r.id, label: r.name })),
  ]);

  protected recipientPickerOptions = computed(() =>
    this.reportRecipients().map((r) => ({ value: r.id, label: r.name })),
  );
  protected reportsCompleted = signal<ReportRow[]>([]);

  protected filteredReports = computed(() => this.reportsCompleted());
  protected documents = signal<DocumentRow[]>([]);
  protected additionalData = signal<AdditionalDataRow[]>([]);

  protected stageBFlag = signal(false);
  protected stageBReportError = signal(false);
  private stageBCheckAttempted = signal(false);

  protected onPartyInvolvementTypeChange(partyId: string, typeId: string): void {
    this.parties.update((list) =>
      list.map((p) => (p.id === partyId ? { ...p, piInvolvementTypeId: typeId } : p)),
    );
  }

  protected onPartyElementChange(el: DataElementRow, value: string): void {
    el.value = value;
    el.completed = !!value;
    this.parties.update((list) => [...list]);
    this.dataElementsDirty.set(true);
  }

  protected onOtherElementChange(el: DataElementRow, value: string): void {
    el.value = value;
    el.completed = !!value;
    this.others.update((list) => [...list]);
    this.dataElementsDirty.set(true);
  }

  protected onMiddleElementChange(el: DataElementRow, value: string): void {
    el.value = value;
    el.completed = !!value;
    this.middleDataElements.update((list) => [...list]);
    this.dataElementsDirty.set(true);
  }

  protected onToggleStageBFlag(checked: boolean): void {
    this.stageBFlag.set(checked);
  }

  /** Stage B, optional (JCCA-169). Editable with Stage B rights until Stage B is closed. */
  protected supervisorNote = signal('');
  protected incidentFollowup = signal('');
  protected stageCFlag = signal(false); // "Follow-Up Note Completed"
  protected stageCNotApplicable = signal(false); // read-only, from LeadershipReviewRequired()

  // ── LOWER SECTION form state ──────────────────────────────────────────────

  protected complianceNote = signal('');
  protected stageDCompliance = signal(false);
  protected pqNote = signal('');
  protected stageDPQ = signal(false);
  protected saving = signal(false);
  protected showClosedModal = signal(false);
  protected showCreationErrors = signal(false);
  protected showInvolvementErrors = signal(false);
  protected showErrors = computed(() => this.stageAFlag() || this.showCreationErrors());
  /** Set when a save is blocked by required data elements left empty (any save, not only creation). */
  protected showDataElementErrors = signal(false);

  /** Required data elements (incident level and per person) must be filled on every save. */
  protected requiredDataElementsComplete = computed(() => {
    const partiesComplete = [...this.parties(), ...this.others()].every((p) =>
      p.dataElements
        .filter((e) => !e.piInvolvementTypeId || e.piInvolvementTypeId === p.piInvolvementTypeId)
        .filter((e) => e.required)
        .every((e) => e.completed),
    );
    return (
      partiesComplete &&
      this.middleDataElements()
        .filter((el) => el.required)
        .every((el) => el.completed)
    );
  });

  // ── Dirty tracking ────────────────────────────────────────────────────────

  private savedSnapshot = signal<string | null>(null);
  private dataElementsDirty = signal(false);

  private currentSnapshot = computed(() =>
    JSON.stringify({
      p: this.program(),
      l: this.location(),
      d: this.incidentDate(),
      n: this.narrative(),
      an: this.additionalNotes(),
      ci: [...this.selectedCategoryIds()].sort(),
      ri: [...this.selectedResponseIds()].sort(),
      pt: this.partiesType(),
      py: this.parties().map((p) => p.personId + '|' + p.piInvolvementTypeId),
      ot: this.others().map((o) => o.personId + '|' + o.piInvolvementTypeId),
      af: this.stageAFlag(),
      sn: this.supervisorNote(),
      fu: this.incidentFollowup(),
      cf: this.stageCFlag(),
      pn: this.pqNote(),
      cn: this.complianceNote(),
      dp: this.stageDPQ(),
      dc: this.stageDCompliance(),
    }),
  );

  isDirty = computed(
    () =>
      this.savedSnapshot() !== null &&
      (this.currentSnapshot() !== this.savedSnapshot() || this.dataElementsDirty()),
  );

  private resetDirty(): void {
    this.savedSnapshot.set(this.currentSnapshot());
    this.dataElementsDirty.set(false);
  }

  private allPartiesHaveInvolvementType = computed(() =>
    [...this.parties(), ...this.others()].every((p) => !!p.piInvolvementTypeId),
  );

  protected partiesCountError = computed<string | null>(() => {
    const type = this.partiesType();
    const count = this.parties().length;
    if (type === 'single' && count < 1) return 'Single party requires exactly 1 person.';
    if (type === 'multiple' && count < 2) return 'Multiple party requires at least 2 people.';
    return null;
  });

  protected stageAHasAnyValue = computed(
    () =>
      !!this.substantiveArea() ||
      !!this.program() ||
      !!this.incidentDate() ||
      !!this.narrative().trim() ||
      !!this.additionalNotes().trim() ||
      this.selectedCategoryIds().length > 0 ||
      this.selectedResponseIds().length > 0 ||
      this.parties().length > 0,
  );

  protected stageAValid = computed(() => {
    const allPartiesValid = [...this.parties(), ...this.others()].every(
      (p) =>
        !!p.piInvolvementTypeId &&
        p.dataElements
          .filter((e) => !e.piInvolvementTypeId || e.piInvolvementTypeId === p.piInvolvementTypeId)
          .filter((e) => e.required)
          .every((e) => e.completed),
    );

    const baseAndPartiesValid =
      !!this.substantiveArea() &&
      !!this.program() &&
      !!this.incidentDate() &&
      this.partiesCountError() === null &&
      this.selectedCategoryIds().length > 0 &&
      allPartiesValid &&
      this.middleDataElements()
        .filter((el) => el.required)
        .every((el) => el.completed);

    // Creation (no ID) or Stage A submission (flag set): full validation required
    if (!this.incident()?.id || (!this.topLocked() && this.stageAFlag())) {
      return baseAndPartiesValid;
    }

    // Stage B/C (topLocked): only party completeness matters for newly added parties
    if (this.topLocked()) {
      return allPartiesValid;
    }

    // Existing Stage A in progress, flag not yet set — allow partial saves
    return true;
  });

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  constructor() {
    effect(() => {
      const programId = this.program();
      const categoryIds = [...this.selectedCategoryIds()].sort();
      untracked(() => this.loadIncidentDataElements(programId, categoryIds));
    });

    effect(() => {
      const categoryIds = this.selectedCategoryIds();
      const taxonomyId = this.situationTaxonomyId();

      if (!categoryIds.length) {
        this.reportRecipients.set([]);
        this.selectedReportRecipient.set('');
        return;
      }

      if (!taxonomyId) return; // wait until program taxonomy is loaded

      this.reportRecipientService
        .getReportRecipientsByCategoryIds(categoryIds, taxonomyId)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (res) => {
            const list = res.items ?? [];
            this.reportRecipients.set(list);
          },
        });
    });

    effect(() => {
      if (!this.stageBCheckAttempted()) return;

      const reportOk = this.reportsCompleted().length > 0;

      if (this.stageBReportError() && reportOk) this.stageBReportError.set(false);

      if (reportOk) {
        this.stageBFlag.set(true);
        this.stageBCheckAttempted.set(false);
      }
    });

    effect(() => {
      const inc = this.incident();
      if (!inc) {
        // Creation mode: capture initial empty state once
        untracked(() => {
          if (this.savedSnapshot() === null) this.savedSnapshot.set(this.currentSnapshot());
        });
        return;
      }
      // Flat fields
      this.program.set(inc.programId ?? '');
      this.location.set(inc.programLocationId ?? '');
      this.incidentDate.set(inc.incidentDate ? inc.incidentDate.slice(0, 16) : '');
      this.narrative.set(inc.narrative ?? '');
      this.additionalNotes.set(inc.response ?? '');

      // Parties type
      const cs = inc.clientsSelection;
      this.partiesType.set(cs === 1 ? 'single' : cs === 2 ? 'multiple' : 'na');

      // Parties involved — split by personTypeCode: CL → clients, rest → others
      const toParty = (pi: (typeof inc.piInvolvements)[0]) => ({
        id: pi.id,
        personId: pi.personId,
        personTypeCode: pi.personTypeCode ?? null,
        piInvolvementTypeId: pi.piInvolvementTypeId,
        name: `${pi.surnames}, ${pi.firstName}`,
        tagline: personTagline(pi),
        role: pi.personTypeName ?? '',
        dataElements: [],
      });
      const clients = inc.piInvolvements.filter((pi) => pi.personTypeCode === 'CL').map(toParty);
      const others = inc.piInvolvements.filter((pi) => pi.personTypeCode !== 'CL').map(toParty);
      this.parties.set(clients);
      this.expandedPartyId.set(clients[0]?.id ?? null);
      this.others.set(others);
      this.expandedOtherId.set(others[0]?.id ?? null);

      // Categories & responses — store IDs for multiselect
      this.selectedCategoryIds.set(inc.incidentCategorizations.map((c) => c.situationCategoryId));
      this.selectedResponseIds.set(inc.incidentResponses.map((r) => r.responseCategoryId));

      // Reports filed
      this.reportsCompleted.set(
        inc.reportFileds.map((r) => ({
          recipientId: r.reportRecipientId,
          recipient: r.reportRecipientName,
          date: r.dateFiled,
          reference: r.referenceCode,
          note: r.note,
        })),
      );

      // Documents
      this.documents.set(
        inc.documents.map((d) => ({
          id: d.id,
          name: d.name,
          type: d.documentTypeName,
          documentUrl: d.documentUrl,
        })),
      );

      // Stage flags
      this.stageAFlag.set(!!inc.stageACompletionFlag);
      this.stageBFlag.set(!!inc.stageBCompletionFlag);
      this.supervisorNote.set(inc.supervisorNote ?? '');
      this.incidentFollowup.set(inc.followUp ?? '');
      this.stageCFlag.set(!!inc.stageCCompletionFlag);

      this.stageCNotApplicable.set(inc.stageCApplicable === false);
      this.complianceNote.set(inc.complianceNote ?? '');
      this.stageDCompliance.set(!!inc.stageDComplianceCompletionFlag);
      this.pqNote.set(inc.pqNote ?? '');
      this.stageDPQ.set(!!inc.stageDPQCompletionFlag);

      // Capture snapshot after all fields are set
      untracked(() => this.resetDirty());

      // Load cascade dropdowns from the stored programId
      if (inc.programId) this.loadFromProgram(inc.programId);
    });
  }

  ngOnInit(): void {
    this.loadSubstantiveAreas();
    // Saved involvements only carry personTypeCode (the API sends no type name), so the
    // "Client" / "Staff" label under each person is resolved from the person types list.
    this.personService
      .getPersonTypes()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (types) =>
          this.personTypeNames.set(
            new Map(
              (types ?? [])
                .filter((t) => !!t.personTypeCode)
                .map((t) => [t.personTypeCode as string, t.name]),
            ),
          ),
      });
    this.programService
      .getPIInvolvementTypes()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (types) => {
          this.rawInvolvementTypes.set(types as PIInvolvementType[]);
        },
      });
  }

  // ── Cascade loading ───────────────────────────────────────────────────────

  private loadSubstantiveAreas(): void {
    this.substantiveAreaLoading.set(true);
    this.programService
      .getSubstantiveAreas()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.substantiveAreaOptions.set(
            res.substantiveAreas.map((a) => ({ value: a.id, label: a.name })),
          );
          this.substantiveAreaLoading.set(false);
        },
        error: () => this.substantiveAreaLoading.set(false),
      });
  }

  /**
   * Edit mode: call getProgramById first to get the substantiveArea,
   * then load programs for that area and populate locations/data elements.
   */
  private loadFromProgram(programId: string): void {
    this.locationLoading.set(true);
    this.programLoading.set(true);
    this.programService
      .getProgramById(programId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (detail) => {
          // Set substantive area from program detail
          const areaId = detail.substantiveArea?.id ?? '';
          this.substantiveArea.set(areaId);

          // Load programs filtered by substantive area/Division
          this.programService
            .getPrograms(undefined, undefined, areaId)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: (res) => {
                this.programOptions.set(res.programs.map((p) => ({ value: p.id, label: p.name })));
                this.programLoading.set(false);
              },
              error: () => this.programLoading.set(false),
            });

          // Set location options and match stored programLocationId
          this.locationOptions.set(
            detail.programLocations.map((pl) => ({
              value: pl.id,
              label: pl.location.locationName,
            })),
          );
          const incLocationId = this.incident()?.programLocationId ?? '';
          const matchedLocation = detail.programLocations.find((pl) => pl.id === incLocationId);
          this.location.set(matchedLocation ? incLocationId : '');

          this.locationLoading.set(false);
          this.loadTaxonomyMembers(detail);

          // Level 1: PI-level data elements for party schema
          this.dataElementService
            .getCdePrograms(programId, 1)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: (res) => {
                const schema = this.mapCdePrograms(res.items);
                this.dataElementSchema.set(schema);
                const inc = this.incident();

                const hydrate = (p: Party) => {
                  const els = schema.map((el) => ({ ...el }));
                  const originalPi = inc?.piInvolvements.find((pi) => pi.id === p.id);
                  if (originalPi) {
                    for (const cdv of originalPi.cdvpiInvolvements) {
                      const el = els.find(
                        (e) => e.configuredDataElementId === cdv.configuredDataElementId,
                      );
                      if (el) this.applyCdvValue(el, cdv);
                    }
                  }
                  return { ...p, dataElements: els };
                };

                this.parties.update((list) => list.map(hydrate));
                this.others.update((list) => list.map(hydrate));
              },
            });
          // Level 0 (incident-level) data elements load from the program + categories effect.
        },
        error: () => {
          this.locationLoading.set(false);
          this.programLoading.set(false);
        },
      });
  }

  private mapCdePrograms(
    cdePrograms: {
      displayOrder: number;
      configuredDataElementId: string;
      piInvolvementTypeId?: string | null;
      requiredOptional?: number | null;
      configuredDataElement: {
        name: string;
        cdeDataTypeId: string;
        cdeDataType: { dataType: string } | null;
        cdeListItems?: { id: string; value: string; displayOrder?: number }[];
      };
    }[],
  ): DataElementRow[] {
    if (!Array.isArray(cdePrograms)) return [];
    return [...cdePrograms]
      .sort((a, b) => a.displayOrder - b.displayOrder)
      .map((cp) => {
        const dataType = cp.configuredDataElement.cdeDataType?.dataType ?? '';
        const listItems = (cp.configuredDataElement.cdeListItems ?? [])
          .slice()
          .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
          .map((i) => i.value);
        const options: SelectOption[] =
          dataType === 'Yes/No'
            ? [
                { value: 'Yes', label: 'Yes' },
                { value: 'No', label: 'No' },
              ]
            : dataType === 'List Item'
              ? (cp.configuredDataElement.cdeListItems ?? [])
                  .slice()
                  .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
                  // Value = item id, so answers reference the item (CDV.CDVListItemId FK) and
                  // survive renames; before, the text was stored and matched by text.
                  .map((i) => ({ value: i.id, label: i.value }))
              : [];
        return {
          order: cp.displayOrder,
          configuredDataElementId: cp.configuredDataElementId,
          label: cp.configuredDataElement.name,
          value: '',
          completed: false,
          required: cp.requiredOptional === 1,
          dataType,
          listItems,
          options,
          piInvolvementTypeId: cp.piInvolvementTypeId ?? null,
        };
      });
  }

  protected onSubstantiveAreaChange(id: string): void {
    this.substantiveArea.set(id);
    this.program.set('');
    this.location.set('');
    this.programOptions.set([]);
    this.locationOptions.set([]);
    if (!id) return;

    this.programLoading.set(true);
    this.programService
      .getPrograms(undefined, undefined, id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.programOptions.set(res.programs.map((p) => ({ value: p.id, label: p.name })));
          this.programLoading.set(false);
        },
        error: () => this.programLoading.set(false),
      });
  }

  protected onProgramChange(id: string): void {
    this.program.set(id);
    this.location.set('');
    this.locationOptions.set([]);
    this.situationTaxonomyMembers.set([]);
    this.responseTaxonomyMembers.set([]);
    this.situationTaxonomyId.set('');
    this.selectedCategoryIds.set([]);
    this.selectedResponseIds.set([]);
    this.reportRecipients.set([]);
    this.selectedReportRecipient.set('');
    if (!id) return;

    this.locationLoading.set(true);
    this.dataElementSchema.set([]);
    this.middleDataElements.set([]);

    this.programService
      .getProgramById(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (detail) => {
          this.locationOptions.set(
            detail.programLocations.map((pl) => ({
              value: pl.id,
              label: pl.location.locationName,
            })),
          );
          this.locationLoading.set(false);
          this.loadTaxonomyMembers(detail);
        },
        error: () => this.locationLoading.set(false),
      });

    this.dataElementService
      .getCdePrograms(id, 1)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          const schema = this.mapCdePrograms(res.items);
          this.dataElementSchema.set(schema);
          this.parties.update((list) =>
            list.map((p) => ({ ...p, dataElements: schema.map((el) => ({ ...el })) })),
          );
        },
        error: (err) => console.error('[incident-record] getCdePrograms error:', err),
      });
    // Level 0 (incident-level) data elements load from the program + categories effect.
  }

  /**
   * Incident-level data elements: the program's CDEs plus the CDEs linked to the selected
   * situation categories (JCCA-159). Reloaded when either changes; answers already entered
   * are kept, and saved answers are applied to rows that appear for the first time.
   */
  private loadIncidentDataElements(programId: string, categoryIds: string[]): void {
    this.incidentDataElementsSub?.unsubscribe();
    if (!programId) {
      this.middleDataElements.set([]);
      return;
    }
    this.incidentDataElementsSub = this.dataElementService
      .getCdePrograms(programId, 0, categoryIds)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          const current = new Map(
            this.middleDataElements().map((r) => [r.configuredDataElementId, r]),
          );
          const inc = this.incident();
          const saved = inc?.programId === programId ? inc.cdvIncidents : [];
          const rows = this.mapCdePrograms(res.items).map((row) => {
            const prev = current.get(row.configuredDataElementId);
            if (prev) return prev;
            const cdv = saved.find(
              (c) => c.configuredDataElementId === row.configuredDataElementId,
            );
            if (cdv) this.applyCdvValue(row, cdv);
            return row;
          });
          this.middleDataElements.set(rows);
        },
        error: (err) => console.error('[incident-record] getCdePrograms error:', err),
      });
  }

  private loadTaxonomyMembers(detail: {
    situationTaxonomy?: { id?: string };
    responseTaxonomy?: { id?: string };
  }): void {
    this.situationTaxonomyId.set(detail.situationTaxonomy?.id ?? '');
    if (detail.situationTaxonomy?.id) {
      this.taxonomyMemberService
        .getSituationTaxonomyMembers(detail.situationTaxonomy.id, { pageSize: 500, pageIndex: 0 })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (res) => this.situationTaxonomyMembers.set(res.situationTaxonomiesMembers),
        });
    }
    if (detail.responseTaxonomy?.id) {
      this.taxonomyMemberService
        .getResponseTaxonomyMembers(detail.responseTaxonomy.id, { pageSize: 500, pageIndex: 0 })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (res) => this.responseTaxonomyMembers.set(res.responseTaxonomiesMembers),
        });
    }
  }

  // ── Interactions ──────────────────────────────────────────────────────────

  protected setPartiesType(type: PartiesType): void {
    this.partiesType.set(type);
    if (type === 'na') this.parties.set([]);
    if (type === 'single' && this.parties().length > 1) this.parties.update((list) => [list[0]]);
  }

  protected onPersonSelected(person: Person): void {
    const newId = `new-${this.nextPartyKey++}`;
    this.parties.update((list) => [
      ...list,
      {
        id: newId,
        personId: person.id,
        personTypeCode: person.personTypeCode,
        piInvolvementTypeId: '',
        name: [person.surnames, person.firstName].filter(Boolean).join(', '),
        tagline: personTagline(person),
        role: person.personTypeName ?? '',
        dataElements: this.dataElementSchema().map((el) => ({ ...el })),
      },
    ]);
    this.expandedPartyId.set(newId);
  }

  protected onOtherSelected(person: Person): void {
    const newId = `other-${this.nextPartyKey++}`;
    this.others.update((list) => [
      ...list,
      {
        id: newId,
        personId: person.id,
        personTypeCode: person.personTypeCode,
        piInvolvementTypeId: '',
        name: [person.surnames, person.firstName].filter(Boolean).join(', '),
        tagline: personTagline(person),
        role: person.personTypeName ?? '',
        dataElements: this.dataElementSchema().map((el) => ({ ...el })),
      },
    ]);
    this.expandedOtherId.set(newId);
  }

  protected onOtherInvolvementTypeChange(otherId: string, typeId: string): void {
    this.others.update((list) =>
      list.map((o) => (o.id === otherId ? { ...o, piInvolvementTypeId: typeId } : o)),
    );
  }

  protected toggleOther(id: string): void {
    this.expandedOtherId.set(this.expandedOtherId() === id ? null : id);
  }

  protected removeOther(id: string): void {
    if (this.expandedOtherId() === id) {
      const remaining = this.others().filter((o) => o.id !== id);
      this.expandedOtherId.set(remaining[0]?.id ?? null);
    }
    this.others.update((list) => list.filter((o) => o.id !== id));
  }

  protected toggleParty(id: string): void {
    this.expandedPartyId.set(this.expandedPartyId() === id ? null : id);
  }

  protected removeParty(id: string): void {
    if (this.expandedPartyId() === id) {
      const remaining = this.parties().filter((p) => p.id !== id);
      this.expandedPartyId.set(remaining[0]?.id ?? null);
    }
    this.parties.update((list) => list.filter((p) => p.id !== id));
  }

  protected onOpenAddReportModal(): void {
    this.editingReportIndex.set(null);
    this.reportDraft.set({ recipientId: '', referenceNumber: '', date: '', note: '' });
    this.showAddReportModal.set(true);
  }

  protected onEditReport(index: number): void {
    const row = this.reportsCompleted()[index];
    if (!row) return;
    this.editingReportIndex.set(index);
    this.reportDraft.set({
      recipientId: row.recipientId,
      referenceNumber: row.reference,
      date: row.date,
      note: row.note,
    });
    this.showAddReportModal.set(true);
  }

  protected onAddReport(): void {
    const d = this.reportDraft();
    if (!d.recipientId) return;

    const recipientName = this.reportRecipients().find((r) => r.id === d.recipientId)?.name ?? '';
    const editIdx = this.editingReportIndex();
    if (editIdx !== null) {
      this.reportsCompleted.update((list) =>
        list.map((r, i) =>
          i === editIdx
            ? {
                ...r,
                recipientId: d.recipientId,
                recipient: recipientName,
                date: d.date,
                reference: d.referenceNumber,
                note: d.note,
              }
            : r,
        ),
      );
    } else {
      this.reportsCompleted.update((list) => [
        ...list,
        {
          recipientId: d.recipientId,
          recipient: recipientName,
          date: d.date,
          reference: d.referenceNumber,
          note: d.note,
        },
      ]);
    }
    const wasEditing = this.editingReportIndex() !== null;
    this.reportDraft.set({ recipientId: '', referenceNumber: '', date: '', note: '' });
    this.editingReportIndex.set(null);
    this.showAddReportModal.set(false);

    if (!this.incident()?.id) return;
    this.saving.set(true);
    this.incidentService
      .updateIncident(this.buildUpdatePayload())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.saving.set(false);
          this.toast.show(wasEditing ? 'Report updated successfully' : 'Report added successfully');
        },
        error: (err) => {
          this.saving.set(false);
          this.toast.show(
            apiErrorMessage(err, wasEditing ? 'Failed to update report' : 'Failed to add report'),
            'error',
          );
        },
      });
  }

  /** Loads a saved CDV into a row (sets value, completed and, for list items, the option). */
  private applyCdvValue(el: DataElementRow, cdv: CdvInvolvement): void {
    el.legacyListValue = null;
    if (el.dataType === 'List Item') {
      el.value = this.resolveListItemValue(el, cdv);
    } else {
      el.value = this.extractCdvValue(cdv);
    }
    el.completed = !!el.value;
  }

  /**
   * List Item answers are stored by id (cdvListItemId). Older answers only have the item text in
   * cdvShortVarchar: map it to the item with that text, or keep it as a legacy option when the
   * item was renamed or removed. They move to the id form the next time the record is saved.
   */
  private resolveListItemValue(el: DataElementRow, cdv: CdvInvolvement): string {
    if (cdv.cdvListItemId) return cdv.cdvListItemId;
    const text = cdv.cdvShortVarchar;
    if (!text) return '';
    const match = el.options.find((o) => o.label === text);
    if (match) return match.value;
    el.legacyListValue = text;
    // New array: rows are shallow copies per party and share `options`.
    el.options = [{ value: text, label: `${text} (no longer in list)` }, ...el.options];
    return text;
  }

  private extractCdvValue(cdv: CdvInvolvement): string {
    if (cdv.cdvYesNo !== null && cdv.cdvYesNo !== undefined) return cdv.cdvYesNo ? 'Yes' : 'No';
    if (cdv.cdvListItemId) return cdv.cdvListItemId;
    if (cdv.cdvDatetime) return cdv.cdvDatetime.slice(0, 10);
    if (cdv.cdvNote) return cdv.cdvNote;
    if (cdv.cdvShortVarchar) return cdv.cdvShortVarchar;
    if (cdv.cdvInteger !== null && cdv.cdvInteger !== undefined) return String(cdv.cdvInteger);
    if (cdv.cdvFloat !== null && cdv.cdvFloat !== undefined) return String(cdv.cdvFloat);
    return '';
  }

  private mapElementToCdv(el: DataElementRow): CdvPayload {
    const val = el.value;
    const KNOWN = ['Datetime', 'Note', 'Integer', 'Float', 'Yes/No', 'List Item'];
    const isList = el.dataType === 'List Item';
    // A legacy value (text with no matching item) goes back as text; anything else is an item id.
    const isLegacyList = isList && !!val && val === el.legacyListValue;
    return {
      configuredDataElementId: el.configuredDataElementId,
      cdvListItemId: isList && val && !isLegacyList ? val : null,
      cdvDatetime: el.dataType === 'Datetime' ? (val ? `${val}T00:00:00` : null) : null,
      cdvNote: el.dataType === 'Note' ? val || null : null,
      cdvInteger: el.dataType === 'Integer' && val ? parseInt(val, 10) : null,
      cdvFloat: el.dataType === 'Float' && val ? parseFloat(val) : null,
      cdvYesNo:
        el.dataType === 'Yes/No' ? (val === 'Yes' ? true : val === 'No' ? false : null) : null,
      cdvShortVarchar: isLegacyList || !KNOWN.includes(el.dataType) ? val || null : null,
    };
  }

  private buildUpdatePayload(): UpdateIncidentPayload {
    const inc = this.incident()!;
    const type = this.partiesType();
    const cs: 0 | 1 | 2 = type === 'single' ? 1 : type === 'multiple' ? 2 : 0;

    return {
      id: inc.id,
      incidentDate: this.incidentDate() || null,
      programId: this.program() || null,
      programLocationId: this.location() || null,
      narrative: this.narrative() || null,
      response: this.additionalNotes() || null,
      followUp: this.incidentFollowup() || null,
      supervisorNote: this.supervisorNote() || null,
      clientsSelection: cs,
      incidentNumber: inc.incidentNumber,
      stageACompletionFlag: this.stageAFlag() ? 1 : null,
      stageACompletionDate: inc.stageACompletionDate,
      stageACompletionStaff: inc.stageACompletionStaff,
      stageBInitiationDate: inc.stageBInitiationDate,
      stageBInitiationStaff: inc.stageBInitiationStaff,
      stageBCompletionFlag: this.stageBFlag() ? 1 : null,
      stageBCompletionDate: inc.stageBCompletionDate,
      stageBCompletionStaff: inc.stageBCompletionStaff,
      stageCApplicable: inc.stageCApplicable,
      stageCInitiationDate: inc.stageCInitiationDate,
      stageCInitiationStaff: inc.stageCInitiationStaff,
      stageCCompletionFlag: this.stageCFlag() ? 1 : null,
      stageCCompletionDate: inc.stageCCompletionDate,
      stageCCompletionStaff: inc.stageCCompletionStaff,
      stageDPQCompletionFlag: this.stageDPQ() ? 1 : null,
      stageDPQSignoffDate: inc.stageDPQSignoffDate,
      stageDPQStaff: inc.stageDPQStaff,
      stageDComplianceCompletionFlag: this.stageDCompliance() ? 1 : null,
      stageDComplianceSignoffDate: inc.stageDComplianceSignoffDate,
      stageDComplianceStaff: inc.stageDComplianceStaff,
      complianceNote: this.complianceNote() || null,
      pqNote: this.pqNote() || null,
      incidentCategorizations: this.selectedCategoryIds().map((id) => ({
        situationCategoryId: id,
      })),
      piInvolvements: [...this.parties(), ...this.others()].map((p) => ({
        id: p.id.startsWith('new-') || p.id.startsWith('other-') ? null : p.id,
        personId: p.personId,
        piInvolvementTypeId: p.piInvolvementTypeId || null,
        cdvpiInvolvements: p.dataElements
          .filter((el) => el.value !== '')
          .map((el) => this.mapElementToCdv(el)),
      })),
      incidentResponses: this.selectedResponseIds().map((id) => ({ responseCategoryId: id })),
      reportFileds: this.reportsCompleted().map((r) => ({
        reportRecipientId: r.recipientId,
        dateFiled: r.date ? r.date.slice(0, 10) : '',
        referenceCode: r.reference,
        note: r.note,
      })),
      cdvIncidents: this.middleDataElements()
        .filter((el) => el.value !== '')
        .map((el) => this.mapElementToCdv(el)),
    };
  }

  protected onReturnToPreviousStage(): void {
    const prev = this.previousStage();
    if (this.returning() || !prev || !this.canReturnToPreviousStage()) return;

    // The update DTO is mapped whole, so null flag + date + staff reopens a stage;
    // the backend re-stamps date/staff when the flag is set again. Clearing cascades
    // from the target stage: later stages are empty at this point, except partial
    // D sign-offs, which must go too (the API rejects any save that keeps a D flag
    // while Stage C has no completion date). Done → D only clears the D sign-offs.
    // The initiation date/staff of the stage being left go too, so the next edit
    // starts that stage again.
    const cleared: Partial<UpdateIncidentPayload> = {
      stageDPQCompletionFlag: null,
      stageDPQSignoffDate: null,
      stageDPQStaff: null,
      stageDComplianceCompletionFlag: null,
      stageDComplianceSignoffDate: null,
      stageDComplianceStaff: null,
    };
    if (prev !== 'D') {
      Object.assign(cleared, {
        stageCCompletionFlag: null,
        stageCCompletionDate: null,
        stageCCompletionStaff: null,
      });
    }
    if (prev === 'A' || prev === 'B') {
      Object.assign(cleared, {
        stageBCompletionFlag: null,
        stageBCompletionDate: null,
        stageBCompletionStaff: null,
        stageCInitiationDate: null,
        stageCInitiationStaff: null,
      });
    }
    if (prev === 'A') {
      Object.assign(cleared, {
        stageACompletionFlag: null,
        stageACompletionDate: null,
        stageACompletionStaff: null,
        stageBInitiationDate: null,
        stageBInitiationStaff: null,
      });
    }

    this.returning.set(true);
    this.incidentService
      .updateIncident({ ...this.buildUpdatePayload(), ...cleared })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.returning.set(false);
          this.showReturnModal.set(false);
          this.resetDirty();
          this.toast.show(`Incident returned to Stage ${prev}`);
          this.saved.emit();
        },
        error: (err) => {
          this.returning.set(false);
          this.showReturnModal.set(false);
          this.toast.show(apiErrorMessage(err, 'Failed to return to the previous stage'), 'error');
        },
      });
  }

  protected onStageCFlagChange(checked: boolean): void {
    this.stageCFlag.set(checked);
  }

  save(): void {
    this.onSave();
  }

  /** Opens the person with a required data element left empty (if any) and scrolls to the first one. */
  private focusFirstMissingDataElement(): void {
    const missing = (p: Party) =>
      p.dataElements.some(
        (e) =>
          e.required &&
          !e.completed &&
          (!e.piInvolvementTypeId || e.piInvolvementTypeId === p.piInvolvementTypeId),
      );
    const party = this.parties().find(missing);
    const other = !party ? this.others().find(missing) : null;
    if (party) this.expandedPartyId.set(party.id);
    else if (other) this.expandedOtherId.set(other.id);
    this.toast.show('Fill in the required data elements.', 'error');
    setTimeout(() => {
      const first = (this.el.nativeElement as HTMLElement).querySelector<HTMLElement>(
        '.bg-red-500.rounded-full',
      );
      first?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }

  private focusFirstError(): void {
    const invalidParty = this.parties().find((p) => !p.piInvolvementTypeId);
    const invalidOther = !invalidParty ? this.others().find((p) => !p.piInvolvementTypeId) : null;
    if (invalidParty) this.expandedPartyId.set(invalidParty.id);
    else if (invalidOther) this.expandedOtherId.set(invalidOther.id);

    setTimeout(() => {
      const first = (this.el.nativeElement as HTMLElement).querySelector<HTMLElement>(
        'p.text-red-500',
      );
      first?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }

  protected onSave(): void {
    if (this.saving()) return;
    if (!this.allPartiesHaveInvolvementType()) {
      this.showInvolvementErrors.set(true);
      this.focusFirstError();
      return;
    }
    if (!this.topDisabled() && !this.requiredDataElementsComplete()) {
      this.showDataElementErrors.set(true);
      this.focusFirstMissingDataElement();
      return;
    }
    const incId = this.incident()?.id;
    const isCreating = !incId;
    const advancing =
      (!this.topLocked() && this.stageAFlag()) || this.stageBFlag() || this.stageCFlag();
    if ((isCreating || advancing) && !this.stageAValid()) {
      if (isCreating) this.showCreationErrors.set(true);
      this.focusFirstError();
      return;
    }
    const inc = this.incident();
    // Stages this save closes (a user with override rights may close more than one at once).
    const closedStages: string[] = [];
    if (!this.topLocked() && this.stageAFlag()) closedStages.push('A');
    if (!inc?.stageBCompletionFlag && this.stageBFlag()) closedStages.push('B');
    if (!inc?.stageCCompletionFlag && this.stageCFlag()) closedStages.push('C');
    const closingStageABC = closedStages.length > 0;
    const closingStageD =
      !(inc?.stageDPQCompletionFlag && inc?.stageDComplianceCompletionFlag) &&
      this.stageDPQ() &&
      this.stageDCompliance();
    this.saving.set(true);
    if (incId) {
      this.incidentService
        .updateIncident(this.buildUpdatePayload())
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.saving.set(false);
            this.resetDirty();
            this.saved.emit();
            if (closingStageABC) {
              this.toast.show(stagesCompletedMessage(closedStages));
              this.router.navigate(['/incidents']);
            } else if (closingStageD) {
              this.showClosedModal.set(true);
            } else {
              this.toast.show('Incident updated successfully');
            }
          },
          error: (err) => {
            this.saving.set(false);
            this.toast.show(apiErrorMessage(err, 'Failed to save incident'), 'error');
            this.saveFailed.emit();
          },
        });
    } else {
      const type = this.partiesType();
      const cs: 0 | 1 | 2 = type === 'single' ? 1 : type === 'multiple' ? 2 : 0;
      const payload: CreateIncidentPayload = {
        incidentDate: this.incidentDate() || null,
        programId: this.program() || null,
        programLocationId: this.location() || null,
        narrative: this.narrative() || null,
        response: this.additionalNotes() || null,
        clientsSelection: cs,
        stageACompletionFlag: this.stageAFlag() ? 1 : null,
        incidentCategorizations: this.selectedCategoryIds().map((id) => ({
          situationCategoryId: id,
        })),
        piInvolvements: [...this.parties(), ...this.others()].map((p) => ({
          personId: p.personId,
          piInvolvementTypeId: p.piInvolvementTypeId || null,
          cdvpiInvolvements: p.dataElements
            .filter((el) => el.value !== '')
            .map((el) => this.mapElementToCdv(el)),
        })),
        incidentResponses: this.selectedResponseIds().map((id) => ({ responseCategoryId: id })),
        reportFileds: this.reportsCompleted().map((r) => ({
          reportRecipientId: r.recipientId,
          dateFiled: r.date ? r.date.slice(0, 10) : '',
          referenceCode: r.reference,
          note: r.note,
        })),
        cdvIncidents: this.middleDataElements()
          .filter((el) => el.value !== '')
          .map((el) => this.mapElementToCdv(el)),
      };
      this.incidentService
        .createIncident(payload)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (inc) => {
            this.saving.set(false);
            this.resetDirty();
            this.toast.show(
              closingStageABC
                ? `Incident created. ${stagesCompletedMessage(closedStages)}`
                : 'Incident created successfully',
            );
            this.router.navigate(['/incidents', inc.id]);
          },
          error: (err) => {
            this.saving.set(false);
            this.toast.show(apiErrorMessage(err, 'Failed to create incident'), 'error');
            this.saveFailed.emit();
          },
        });
    }
  }
}

/** "Stage A Completed", or "Stages A and B Completed" when a save closes more than one. */
function stagesCompletedMessage(stages: string[]): string {
  if (stages.length === 1) return `Stage ${stages[0]} Completed`;
  const last = stages[stages.length - 1];
  return `Stages ${stages.slice(0, -1).join(', ')} and ${last} Completed`;
}
